import { FormLabel, FormErrorMessage, Flex, Input, FormControl, InputGroup, Select, Checkbox, Divider, FormHelperText } from "@chakra-ui/react";
import { Button, useConfirm } from "am-tax-fe-core";
import { ChangeEvent, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { User } from "../../../api";
import { useUpdateSystemAdminStatus } from "../../../api/users/userQueries";

interface Props {
    onClose: () => void;
    userData: User | undefined;
    existingUsers: User[] | undefined;
    isEdit: boolean;
}

type FormFields = {
    firstName: string;
    lastName: string;
    userEmail: string;
    userName: string;
    isSystemAdmin: boolean;
};

export const CreateOrEditUserForm = ({ userData, existingUsers, isEdit, onClose }: Props) => {
    const { mutate: updateSystemAdminStatus } = useUpdateSystemAdminStatus();
    // const { data: allusers, isLoading: isLoadingusers } = useusers();
    const [allusers] = useState<
        {
            id: string;
            name: string;
        }[]
    >([
        {
            id: "1",
            name: "Test user",
        },
    ]);

    const formValues = {
        firstName: userData?.firstName ?? "",
        lastName: userData?.lastName ?? "",
        userEmail: userData?.email ?? "",
        userName: userData?.id ?? "",
        isSystemAdmin: userData?.isSystemAdmin ?? false,
    };

    const {
        register,
        handleSubmit,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<FormFields>({ values: formValues });

    const prompt = "Are you sure you want to delete the user?";
    const { ConfirmDialog } = useConfirm({ title: "Delete User", prompt: prompt });

    const onSubmit: SubmitHandler<FormFields> = async data => {
        if (isEdit && userData?.azureAdObjectId) {
            updateSystemAdminStatus(
                {
                    azureAdObjectId: userData.azureAdObjectId,
                    request: {
                        isSystemAdmin: data.isSystemAdmin,
                    },
                },
                {
                    onSuccess: () => {
                        onClose();
                    },
                },
            );
        }
    };

    const handleUserEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        if (existingUsers) {
            const existingUserEmails = existingUsers.map(eu => eu.email);
            const hasUserEmail = existingUserEmails ? existingUserEmails.some(u => u === value.trim()) : false;

            if (hasUserEmail)
                setError("userEmail", {
                    type: "manual",
                    message: "User with this email already exists. ",
                });
            else clearErrors("userEmail");
        }
    };

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Flex gap="2rem" flexFlow="column nowrap">
                    {!isEdit && (
                        <>
                            <Flex gap="1rem">
                                <FormControl isInvalid={!!errors?.firstName}>
                                    <FormLabel fontWeight="400">First name:</FormLabel>
                                    <Input
                                        {...register("firstName", {
                                            required: "First name is required.",
                                            validate: value => value.trim() !== "" || "First name is required.",
                                        })}
                                    />
                                    <FormErrorMessage>{errors.firstName?.message}</FormErrorMessage>
                                </FormControl>
                                <FormControl isInvalid={!!errors?.lastName}>
                                    <FormLabel fontWeight="400">Last name:</FormLabel>
                                    <Input
                                        {...register("lastName", {
                                            required: "Last name is required.",
                                            validate: value => value.trim() !== "" || "Last name is required.",
                                        })}
                                    />
                                    <FormErrorMessage>{errors.lastName?.message}</FormErrorMessage>
                                </FormControl>
                            </Flex>
                            <FormControl isInvalid={!!errors?.userEmail}>
                                <FormLabel fontWeight="400">Email:</FormLabel>
                                <InputGroup>
                                    <Input
                                        {...register("userEmail", {
                                            required: "Email is required.",
                                            pattern: {
                                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                                message: "Please enter a valid email eg. username@domain.ext",
                                            },
                                        })}
                                        placeholder="username@domain.ext"
                                        onChange={handleUserEmailChange}
                                    />
                                </InputGroup>
                                <FormErrorMessage>{errors.userEmail?.message}</FormErrorMessage>
                            </FormControl>

                            <FormControl isInvalid={!!errors?.userName}>
                                <FormLabel fontWeight="400">user:</FormLabel>
                                <Select {...register("userName")} placeholder="Select user">
                                    {allusers?.map(user => (
                                        <option key={user.id} value={user.id}>
                                            {user.name}
                                        </option>
                                    ))}
                                </Select>
                                <FormErrorMessage>{errors.userName?.message}</FormErrorMessage>
                            </FormControl>

                            <Divider />
                        </>
                    )}

                    {isEdit && (
                        <>
                            <FormControl mb={4}>
                                <FormLabel fontWeight="400">User:</FormLabel>
                                <Input value={`${userData?.firstName || ""} ${userData?.lastName || ""}`.trim()} isReadOnly bg="gray.100" />
                            </FormControl>
                            <FormControl mb={4}>
                                <FormLabel fontWeight="400">Email:</FormLabel>
                                <Input value={userData?.email || ""} isReadOnly bg="gray.100" />
                            </FormControl>
                        </>
                    )}

                    <FormControl isInvalid={!!errors?.isSystemAdmin}>
                        <FormLabel fontWeight="400">System Admin:</FormLabel>
                        <Checkbox {...register("isSystemAdmin")} defaultValue={0}>
                            Grant this user System Admin permissions
                        </Checkbox>
                        <FormHelperText>
                            System Admins have access to assign other system admins and edit client/jurisdiction settings. Use with caution.
                        </FormHelperText>
                    </FormControl>

                    <Flex marginBottom=".75rem" justifyContent="flex-end">
                        <Button type="submit" variant="primary">
                            {isEdit ? "Update System Admin Status" : "Add User"}
                        </Button>
                    </Flex>
                </Flex>
            </form>
            <ConfirmDialog />
        </>
    );
};
